/**
 * Certificate Print Page
 * Displays the marriage certificate for printing
 */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePageTitle } from "../contexts/PageTitleContext";
import { certificateService } from "../services/api/certificate.service";
import { applicantService } from "../services/api/applicant.service";
import { settingsService } from "../services/api/settings.service";
import type {
  CertificateApplication,
  County,
} from "../types/certificate.types";
import type { Applicant } from "../types/applicant.types";
import { ApplicantType } from "../types/applicant.types";
import BusyOverlay from "../components/common/BusyOverlay";
import {
  ApplicationPermissionId,
  HasPermission,
  ModuleId,
} from "../utils/permissions";
import { CountyMarriageCert } from "../components/CountyMarriageCert";
import HQMarriageCert from "../components/HQMaarriageCert";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";

export const CertificatePrint: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [application, setApplication] = useState<CertificateApplication | null>(
    null,
  );
  const [groomApplicant, setGroomApplicant] = useState<Applicant | null>(null);
  const [brideApplicant, setBrideApplicant] = useState<Applicant | null>(null);
  const [counties, setCounties] = useState<County[]>([]);
  const [ministryName, setMinistryName] = useState<string>(
    "Ministry of Local Government",
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle("Print Certificate", "Print marriage certificate");
    fetchData();
    fetchSettings();
    fetchCounties();
  }, [id, setPageTitle]);

  const fetchData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError("");

      // Fetch application
      const appData = await certificateService.getCertificateApplicationById(
        parseInt(id, 10),
      );
      setApplication(appData);

      // Fetch applicants
      const applicants = await applicantService.getApplicantsByCertificateId(
        parseInt(id, 10),
      );

      const groom = applicants.find(
        (a: Applicant) => a.applicantType === ApplicantType.Groom,
      );
      const bride = applicants.find(
        (a: Applicant) => a.applicantType === ApplicantType.Bride,
      );

      setGroomApplicant(groom || null);
      setBrideApplicant(bride || null);
    } catch (err: any) {
      console.error("Error fetching data:", err);
      setError(err?.message || "Failed to load certificate data");
    } finally {
      setLoading(false);
    }
  };

  const fetchCounties = async () => {
    try {
      const data = await certificateService.getCounties();
      setCounties(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching counties:", err);
    }
  };

  const fetchSettings = async () => {
    try {
      const settings = await settingsService.getSettings();
      const ministrySetting = settings.find(
        (s) => s.key === "ministryName" && s.category === "general",
      );
      if (ministrySetting?.value) {
        setMinistryName(ministrySetting.value);
      }
    } catch (err: any) {
      console.error("Error fetching settings:", err);
      // Keep default value on error
    }
  };

  if (!HasPermission(ModuleId.Weddings, ApplicationPermissionId.Print)) {
    return (
      <div
        className={`dashboard-body ${
          sidebarOpen ? "sidebar-open" : "sidebar-closed"
        }`}
      >
        <Topbar
          onMenuToggle={() => setSidebarOpen((prev) => !prev)}
          sidebarOpen={sidebarOpen}
        />
        <Sidebar isOpen={sidebarOpen} />
        <main className="main-content">
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>You do not have permission to print certificates</span>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div
      className={`dashboard-body ${
        sidebarOpen ? "sidebar-open" : "sidebar-closed"
      }`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((prev) => !prev)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />
      <main className="main-content">
        <BusyOverlay isVisible={loading} message="Loading certificate..." />

        {error && (
          <div
            className="alert alert-error"
            style={{
              margin: "20px",
              maxWidth: "800px",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <span className="alert-icon">⚠️</span>
            <span>{error}</span>
            <button onClick={() => setError("")} className="alert-close">
              ✕
            </button>
          </div>
        )}

        {!loading && !application && (
          <div
            className="alert alert-error"
            style={{
              margin: "20px",
              maxWidth: "800px",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <span className="alert-icon">⚠️</span>
            <span>Certificate application not found</span>
            <button
              onClick={() => window.location.reload()}
              className="alert-close"
            >
              ↻
            </button>
          </div>
        )}

        {!loading && application && (!groomApplicant || !brideApplicant) && (
          <div
            className="alert alert-error"
            style={{
              margin: "20px",
              maxWidth: "800px",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <span className="alert-icon">⚠️</span>
            <span>
              Applicant information incomplete. Groom and bride data are
              required to display the certificate.
            </span>
            <button onClick={() => setError("")} className="alert-close">
              ✕
            </button>
          </div>
        )}

        {application && groomApplicant && brideApplicant && (
          <>
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                background: "#fff",
              }}
              className="no-print"
            >
              <button
                onClick={() => window.print()}
                style={{
                  padding: "12px 32px",
                  fontSize: "16px",
                  background: "#3b82f6",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "600",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                🖨️ Print Certificate
              </button>
              <button
                onClick={() => navigate("/marriages/print")}
                style={{
                  padding: "12px 32px",
                  fontSize: "16px",
                  background: "#6b7280",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "600",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  marginLeft: "10px",
                }}
              >
                ← Back
              </button>
            </div>

            {application.processAtHeadquarters ? (
              <HQMarriageCert
                groomName={`${groomApplicant.firstName} ${groomApplicant.middleName || ""} ${groomApplicant.lastName}`.trim()}
                groomDateOfBirth={groomApplicant.dateOfBirth || ""}
                groomPlaceOfBirth={groomApplicant.birthTown || ""}
                groomCountyOfBirth={
                  counties.find((c) => c.id === groomApplicant.birthCountyId)
                    ?.name || ""
                }
                groomFatherName={groomApplicant.fatherName || ""}
                groomMotherName={groomApplicant.motherName || ""}
                brideName={`${brideApplicant.firstName} ${brideApplicant.middleName || ""} ${brideApplicant.lastName}`.trim()}
                brideProposedName={brideApplicant.brideProposedName || ""}
                brideDateOfBirth={brideApplicant.dateOfBirth || ""}
                bridePlaceOfBirth={brideApplicant.birthTown || ""}
                brideCountyOfBirth={
                  counties.find((c) => c.id === brideApplicant.birthCountyId)
                    ?.name || ""
                }
                brideFatherName={brideApplicant.fatherName || ""}
                brideMotherName={brideApplicant.motherName || ""}
                placeOfWedding={application.placeOfWedding || ""}
                dateOfWedding={application.dateOfWedding || ""}
                countyName={application.countyName || ""}
                certificateCode={application.formNo || ""}
                revenueNumber=""
                ministryName={ministryName}
                groomPhotoUrl={
                  groomApplicant.photoUrl || groomApplicant.profileImagePath
                }
                bridePhotoUrl={
                  brideApplicant.photoUrl || brideApplicant.profileImagePath
                }
                certificateApplicationId={application.id}
              />
            ) : (
              <CountyMarriageCert
                groomName={`${groomApplicant.firstName} ${groomApplicant.middleName || ""} ${groomApplicant.lastName}`.trim()}
                groomDateOfBirth={groomApplicant.dateOfBirth || ""}
                groomPlaceOfBirth={groomApplicant.birthTown || ""}
                groomCountyOfBirth={
                  counties.find((c) => c.id === groomApplicant.birthCountyId)
                    ?.name || ""
                }
                groomFatherName={groomApplicant.fatherName || ""}
                groomMotherName={groomApplicant.motherName || ""}
                brideName={`${brideApplicant.firstName} ${brideApplicant.middleName || ""} ${brideApplicant.lastName}`.trim()}
                brideProposedName={brideApplicant.brideProposedName || ""}
                brideDateOfBirth={brideApplicant.dateOfBirth || ""}
                bridePlaceOfBirth={brideApplicant.birthTown || ""}
                brideCountyOfBirth={
                  counties.find((c) => c.id === brideApplicant.birthCountyId)
                    ?.name || ""
                }
                brideFatherName={brideApplicant.fatherName || ""}
                brideMotherName={brideApplicant.motherName || ""}
                placeOfWedding={application.placeOfWedding || ""}
                dateOfWedding={application.dateOfWedding || ""}
                countyName={application.countyName || ""}
                certificateCode={application.formNo || ""}
                revenueNumber=""
                ministryName={ministryName}
                groomPhotoUrl={
                  groomApplicant.photoUrl || groomApplicant.profileImagePath
                }
                bridePhotoUrl={
                  brideApplicant.photoUrl || brideApplicant.profileImagePath
                }
                certificateApplicationId={application.id}
              />
            )}
          </>
        )}
      </main>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .dashboard-body {
            margin: 0 !important;
            padding: 0 !important;
          }
          .main-content {
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>
      <Footer />
    </div>
  );
};

export default CertificatePrint;
